import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';

export interface FeatureUsage {
  allowed: boolean;
  current: number;
  limit: number;
  remaining: number;
  resetsOn: string;
  error?: string;
}

export interface UsageStats {
  logo_generation: FeatureUsage;
  text_to_image: FeatureUsage;
  business_plan: FeatureUsage;
}

interface UsageContextType {
  stats: UsageStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isLoading: boolean;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

export const UsageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      
      // Get token AND userId from Supabase session
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const userId = sessionData?.session?.user?.id;
      
      if (!token || !userId) {
        console.warn('No auth token or userId available for usage stats');
        setLoading(false);
        return;
      }

      // Backend requires userId as query parameter
      const url = new URL(`${ENV.API_URL}/api/usage/stats`);
      url.searchParams.set('userId', userId);

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      } else {
        // Don't show errors to user - fail silently
        console.warn('Failed to fetch usage stats:', response.status);
        setStats(null);
      }
    } catch (err) {
      // Fail silently - usage tracking shouldn't break the app
      console.warn('Error fetching usage stats:', err);
      setStats(null);
      setError(null); // Don't expose errors to UI
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <UsageContext.Provider value={{ stats, loading, error, refresh, isLoading: loading }}>
      {children}
    </UsageContext.Provider>
  );
};

export const useUsage = () => {
  const context = useContext(UsageContext);
  if (!context) {
    throw new Error('useUsage must be used within UsageProvider');
  }
  return context;
};
